import * as geohash from 'ngeohash';
import { MapBounds } from '../types';

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';
import type { Feature, Polygon, MultiPolygon } from 'geojson';

/**
 * Get geohash range for a bounding box (for Firestore range queries).
 */
export function getGeohashRange(bounds: MapBounds): { lower: string; upper: string } {
  const sw = geohash.encode(bounds.southWest.latitude, bounds.southWest.longitude, 7);
  const ne = geohash.encode(bounds.northEast.latitude, bounds.northEast.longitude, 7);
  return { lower: sw, upper: ne + '\uf8ff' };
}

/**
 * Haversine distance formula — returns distance in kilometers.
 */
export function getDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Convert a Leaflet-style integer zoom to the latitudeDelta/longitudeDelta
 * used by react-native-maps regions. Leaflet zoom 17 ≈ delta 0.005 at
 * mid-latitudes; scales with 2^-zoom.
 */
export function zoomToDelta(zoom: number): number {
  return 360 / Math.pow(2, zoom);
}

/**
 * Point-in-polygon test against a GeoJSON Feature. Accepts Polygon or
 * MultiPolygon. Returns false if the feature is null/undefined so callers
 * can treat "polygon not yet loaded" as "not contained."
 */
export function pointInPolygon(
  latitude: number,
  longitude: number,
  feature: Feature<Polygon | MultiPolygon> | null | undefined
): boolean {
  if (!feature) return false;
  return booleanPointInPolygon(point([longitude, latitude]), feature);
}
